import {
    List, Datagrid, TextField, DateField, BooleanField, ReferenceField, EmailField,
    Show, SimpleShowLayout, SimpleForm, TextInput, ReferenceInput, SelectInput, ReferenceArrayInput, SelectArrayInput,
    Create, required, useRecordContext, FunctionField, DeleteButton, useNotify, useRefresh,
    Button, useDataProvider, TopToolbar, useGetMany, CreateButton, ListButton, useLocaleState
} from 'react-admin';
import { useState } from 'react';
import { Chip, Box, MenuItem, TextField as MuiTextField } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';

import { InvitationImportButton } from '../components/InvitationImportButton';
import { invitationStatus } from './invitationStatus';

// Produkt-Auswahl fuer den Rollen-Filter (Brand-Schreibweise: klein mit
// Doppelpunkt; technische Keys bleiben academy/azubiweb).
const APPLICATION_CHOICES = [
    { id: 'academy', name: 'online:academy' },
    { id: 'azubiweb', name: 'azubi:web' },
];
const InvitationTitle = () => {
    const record = useRecordContext();
    return <span>{record ? `Einladung: ${record.email}` : ''}</span>;
};

const RolesField = (_props: { label?: string }) => {
    const record = useRecordContext();
    const { data, isLoading } = useGetMany(
        'roles',
        { ids: record?.roles || [] },
        { enabled: !!record?.roles && record.roles.length > 0 }
    );

    if (isLoading) return <span>Lädt …</span>;
    if (!data || data.length === 0) return <span>-</span>;

    return (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {data.map((role: any) => (
                <Chip key={role.id} label={role.name} size="small" />
            ))}
        </Box>
    );
};

const ResendInvitationButton = () => {
    const record = useRecordContext();
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const refresh = useRefresh();

    if (!record || record.used) {
        return null;
    }

    const handleResend = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_SSO_URL}/api/invitations/${record.id}/resend`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                    'Content-Type': 'application/json',
                },
            });

            if (response.ok) {
                notify('Einladung wurde erneut versendet', { type: 'success' });
                refresh();
            } else {
                const error = await response.json();
                notify(error.error || 'Einladung konnte nicht erneut versendet werden', { type: 'error' });
            }
        } catch (error) {
            notify('Einladung konnte nicht erneut versendet werden', { type: 'error' });
        }
    };

    return (
        <Button
            label="Einladung erneut senden"
            onClick={handleResend}
            startIcon={<SendIcon />}
        />
    );
};

const InvitationShowActions = () => (
    <TopToolbar>
        <ListButton />
        <ResendInvitationButton />
    </TopToolbar>
);


const InvitationListActions = () => (
    <TopToolbar>
        <InvitationImportButton />
        <CreateButton />
    </TopToolbar>
);
export const InvitationList = () => {
    const [locale] = useLocaleState();

    return (
        <List actions={<InvitationListActions />}>
            <Datagrid rowClick="show">
                <TextField source="id" />
                <EmailField source="email" />
                <ReferenceField source="company" reference="companies" link="show">
                    <TextField source="displayName" />
                </ReferenceField>
                <RolesField label="Rollen" />
                <DateField source="createdAt" showTime locales={locale} />
                <DateField source="expiresAt" showTime locales={locale} />
                <BooleanField source="used" />
                <FunctionField
                    label="Status"
                    render={(record: any) => {
                        const { label, color } = invitationStatus(record);

                        return <Chip label={label} color={color} size="small" />;
                    }}
                />
                <DeleteButton />
            </Datagrid>
        </List>
    );
};

export const InvitationShow = () => {
    const [locale] = useLocaleState();

    return (
        <Show title={<InvitationTitle />} actions={<InvitationShowActions />}>
            <SimpleShowLayout>
                <TextField source="id" />
                <EmailField source="email" />
                <ReferenceField source="company" reference="companies" link="show">
                    <TextField source="displayName" />
                </ReferenceField>
                <RolesField label="Rollen" />
                <DateField source="createdAt" showTime locales={locale} />
                <DateField source="expiresAt" showTime locales={locale} />
                <BooleanField source="used" />
                <DateField source="usedAt" showTime locales={locale} />
                <ReferenceField source="registeredUser" reference="users" link="show">
                    <TextField source="email" />
                </ReferenceField>
            </SimpleShowLayout>
        </Show>
    );
};

export const InvitationCreate = () => {
    // Reiner UI-Filter (kein Formularfeld): schraenkt die Rollen-Auswahl auf
    // ein Produkt + globale Rollen ein (SSO: RoleCollectionProvider,
    // ?forApplication=...). Wird NICHT an die API uebermittelt.
    const [productFilter, setProductFilter] = useState<string>('all');

    return (
        <Create redirect="list" actions={<TopToolbar><ListButton /></TopToolbar>}>
            <SimpleForm>
                <TextInput
                    source="email"
                    label="E-Mail-Adresse"
                    validate={required()}
                    type="email"
                    fullWidth
                    helperText="E-Mail-Adresse der Person, die eingeladen werden soll"
                />
                <ReferenceInput source="company" reference="companies" label="Unternehmen" sort={{ field: 'name', order: 'ASC' }}>
                    <SelectInput
                        optionText="displayName"
                        label="Unternehmen"
                        validate={required()}
                        fullWidth
                        helperText="Unternehmen für diese Einladung auswählen"
                    />
                </ReferenceInput>
                <MuiTextField
                    select
                    label="Für welches Produkt einladen?"
                    value={productFilter}
                    onChange={(e) => setProductFilter(e.target.value)}
                    helperText="Filtert die Rollen-Auswahl (globale Rollen bleiben immer sichtbar)"
                    fullWidth
                    sx={{ mb: 2 }}
                >
                    <MenuItem value="all">Alle Produkte</MenuItem>
                    {APPLICATION_CHOICES.map((choice) => (
                        <MenuItem key={choice.id} value={choice.id}>{choice.name}</MenuItem>
                    ))}
                </MuiTextField>
                <ReferenceArrayInput
                    source="roles"
                    reference="roles"
                    sort={{ field: 'name', order: 'ASC' }}
                    filter={productFilter !== 'all' ? { forApplication: productFilter } : {}}
                >
                    <SelectArrayInput
                        optionText={(record: any) => record?.description ? `${record.name} — ${record.description}` : record?.name}
                        label="Rollen"
                        fullWidth
                        helperText="Rollen für den eingeladenen Benutzer auswählen"
                        sx={{ '& .MuiSelect-select': { minHeight: '1.4375em', paddingTop: '16.5px', paddingBottom: '16.5px' } }}
                    />
                </ReferenceArrayInput>
            </SimpleForm>
        </Create>
    );
};
